import { ActionIcon, Alert, Anchor, Button, Group, Menu, Modal, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { AdPlatform } from '@marketing-tool/shared';
import {
    IconChevronRight,
    IconCopy,
    IconDotsVertical,
    IconExternalLink,
    IconPencil,
    IconPlus,
    IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { StatusBadge } from '../../components/StatusBadge';
import { Table } from '../../components/Table';
import { useAdGroups } from '../../data/game-report';
import type { AdGroup, Campaign } from '../../data/game-report/types';
import * as remote from '../../data/remote-data';
import { trpc } from '../../lib/trpc';
import { BulkEditModal } from '../../platforms/line/components/BulkEditModal';
import type { BulkEditFormData } from '../../platforms/line/components/BulkEditModal';
import { CreateAdGroupForm } from '../../platforms/line/components/CreateAdGroupForm';
import type { CreateAdGroupFormData } from '../../platforms/line/components/CreateAdGroupForm';
import { EditAdGroupForm } from '../../platforms/line/components/EditAdGroupForm';
import type { EditAdGroupFormData } from '../../platforms/line/components/EditAdGroupForm';

import { confirmAndDeleteTrpc, OperationErrorAlert } from './mutations';
import type { OperationError } from './mutations';

type AdGroupsTabContentProps = {
    gameId: string;
    platform: AdPlatform;
    campaignId?: string;
    onDataLoaded?: (adGroups: AdGroup[]) => void;
    selectedCampaign: Campaign | undefined;
    getAdGroupUrl: (adGroupId: string | number) => string;
    onAdGroupClick: (adGroup: AdGroup) => void;
};

export const AdGroupsTabContent = ({
    gameId,
    platform,
    campaignId,
    onDataLoaded,
    selectedCampaign,
    getAdGroupUrl,
    onAdGroupClick,
}: AdGroupsTabContentProps) => {
    const adGroups = useAdGroups(gameId, platform, campaignId);

    useEffect(() => {
        if (adGroups.data.tag === 'Success' && onDataLoaded) {
            onDataLoaded(adGroups.data.value);
        }
    }, [adGroups.data, onDataLoaded]);

    // --- Modal state ---
    const [createModal, setCreateModal] = useState<{ source?: AdGroup } | null>(null);
    const [editing, setEditing] = useState<AdGroup | null>(null);
    const [bulkEditing, setBulkEditing] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

    // --- Per-operation error/loading state ---
    const [createError, setCreateError] = useState<OperationError>(null);
    const [editError, setEditError] = useState<OperationError>(null);
    const [bulkEditError, setBulkEditError] = useState<OperationError>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- Handlers ---

    const closeCreate = () => {
        setCreateModal(null);
        setCreateError(null);
    };

    const handleCreate = async (data: CreateAdGroupFormData) => {
        setIsSubmitting(true);
        setCreateError(null);
        try {
            await trpc.line.adGroups.create.mutate({
                gameId,
                campaignId: data.campaignId,
                name: data.name,
                dailyBudgetMicro: data.dailyBudgetMicro,
                targetingMode: data.targetingMode,
                country: data.country,
                genders: data.genders,
                ageMin: data.ageMin,
                ageMax: data.ageMax,
                userOs: data.userOs,
                placements: data.placements,
                autoBidType: data.autoBidType,
                bidStrategy: data.bidStrategy,
            });
            notifications.show({ title: 'Success', message: 'Ad group created', color: 'green' });
            closeCreate();
            void adGroups.load();
        } catch (error) {
            setCreateError({
                message: error instanceof Error ? error.message : 'Failed to create ad group. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeEdit = () => {
        setEditing(null);
        setEditError(null);
    };

    const handleUpdate = async (data: EditAdGroupFormData) => {
        setIsSubmitting(true);
        setEditError(null);
        try {
            const { id, ...updateData } = data;
            await trpc.line.adGroups.update.mutate({
                gameId,
                updates: { [id]: updateData },
            });
            notifications.show({ title: 'Success', message: 'Ad group updated', color: 'green' });
            closeEdit();
            void adGroups.load();
        } catch (error) {
            setEditError({
                message: error instanceof Error ? error.message : 'Failed to update ad group. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = (adGroupId: string | number) =>
        confirmAndDeleteTrpc({
            title: 'Delete ad group',
            message: 'Are you sure you want to delete this ad group? This action cannot be undone.',
            deleteFn: () => trpc.line.adGroups.delete.mutate({ gameId, adGroupIds: [String(adGroupId)] }),
            successMessage: 'Ad group deleted',
            onSuccess: () => void adGroups.load(),
        });

    const handleBulkDelete = () => {
        if (selectedIds.size === 0) return;
        confirmAndDeleteTrpc({
            title: 'Delete ad groups',
            message: `Are you sure you want to delete ${selectedIds.size} ad group(s)? This action cannot be undone.`,
            deleteFn: () =>
                trpc.line.adGroups.delete.mutate({ gameId, adGroupIds: Array.from(selectedIds).map(String) }),
            successMessage: `${selectedIds.size} ad group(s) deleted`,
            onSuccess: () => {
                setSelectedIds(new Set());
                void adGroups.load();
            },
        });
    };

    const closeBulkEdit = () => {
        setBulkEditing(false);
        setBulkEditError(null);
    };

    const handleBulkUpdate = async (data: BulkEditFormData) => {
        setIsSubmitting(true);
        setBulkEditError(null);
        try {
            const updates = Object.fromEntries(Array.from(selectedIds).map((id) => [id, data]));
            await trpc.line.adGroups.update.mutate({ gameId, updates });
            notifications.show({ title: 'Success', message: 'Ad groups updated', color: 'green' });
            closeBulkEdit();
            setSelectedIds(new Set());
            void adGroups.load();
        } catch (error) {
            setBulkEditError({
                message: error instanceof Error ? error.message : 'Failed to update ad groups. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Render ---

    if (remote.isNotAsked(adGroups.data)) return null;
    if (remote.isFailure(adGroups.data)) {
        return (
            <Alert color="red" title="Error loading ad groups">
                {adGroups.data.value}
            </Alert>
        );
    }

    return (
        <>
            <Group justify="space-between" mb="md">
                <Text size="sm" fw={600}>
                    Ad Groups
                </Text>
                {!selectedCampaign ? (
                    <Tooltip label="Select a campaign first to create an ad group">
                        <span>
                            <Button
                                size="sm"
                                onClick={() => setCreateModal({})}
                                disabled
                                leftSection={<IconPlus size={16} />}
                            >
                                Create Ad Group
                            </Button>
                        </span>
                    </Tooltip>
                ) : (
                    <Button size="sm" onClick={() => setCreateModal({})} leftSection={<IconPlus size={16} />}>
                        Create Ad Group
                    </Button>
                )}
            </Group>

            <Table
                storageKey="ad-groups"
                loading={remote.isLoading(adGroups.data)}
                id={(adGroup) => adGroup.id}
                density="xs"
                columns={[
                    {
                        name: 'Ad Group',
                        render: (adGroup) => (
                            <Anchor
                                href={getAdGroupUrl(adGroup.id)}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onAdGroupClick(adGroup);
                                }}
                                underline="hover"
                                fw={600}
                                inherit
                                style={{ minWidth: 0 }}
                            >
                                <Group gap={4} wrap="nowrap">
                                    <Text truncate inherit>
                                        {adGroup.name}
                                    </Text>
                                    <IconChevronRight size={14} style={{ flexShrink: 0 }} />
                                </Group>
                            </Anchor>
                        ),
                        sort: (a, b) => a.name.localeCompare(b.name),
                    },
                    {
                        name: 'Status',
                        type: 'status',
                        width: 120,
                        render: (adGroup) => {
                            const status =
                                'deliveryStatus' in adGroup ? adGroup.deliveryStatus : adGroup.effectiveStatus;
                            return <StatusBadge status={status} />;
                        },
                        sort: (a, b) => {
                            const statusA = 'deliveryStatus' in a ? a.deliveryStatus : a.effectiveStatus;
                            const statusB = 'deliveryStatus' in b ? b.deliveryStatus : b.effectiveStatus;
                            return statusA.localeCompare(statusB);
                        },
                    },
                    {
                        name: 'Daily Budget',
                        type: 'currency',
                        width: 120,
                        render: (adGroup) => {
                            const budgetJpy = adGroup.dailyBudgetMicro ? adGroup.dailyBudgetMicro / 1_000_000 : null;
                            return budgetJpy !== null ? `¥${budgetJpy.toLocaleString()}` : '—';
                        },
                        sort: (a, b) => (a.dailyBudgetMicro ?? 0) - (b.dailyBudgetMicro ?? 0),
                    },
                    {
                        name: 'Creation Date',
                        type: 'date',
                        width: 120,
                        render: (adGroup) => new Date(adGroup.createdDate).toLocaleDateString(),
                        sort: (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
                    },
                    {
                        name: 'Actions',
                        type: 'actions',
                        width: 50,
                        render: (adGroup) => (
                            <Menu position="bottom-end" withinPortal>
                                <Menu.Target>
                                    <ActionIcon
                                        variant="subtle"
                                        aria-label="Row actions"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <IconDotsVertical size={16} />
                                    </ActionIcon>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    {adGroup.externalAdsManagerUrl && (
                                        <Menu.Item
                                            component="a"
                                            href={adGroup.externalAdsManagerUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            leftSection={<IconExternalLink size={14} />}
                                        >
                                            Open in Ads Manager
                                        </Menu.Item>
                                    )}
                                    <Menu.Item
                                        leftSection={<IconCopy size={14} />}
                                        onClick={() => setCreateModal({ source: adGroup })}
                                    >
                                        Duplicate
                                    </Menu.Item>
                                    <Menu.Item
                                        leftSection={<IconPencil size={14} />}
                                        onClick={() => setEditing(adGroup)}
                                    >
                                        Edit
                                    </Menu.Item>
                                    <Menu.Divider />
                                    <Menu.Item
                                        leftSection={<IconTrash size={14} />}
                                        color="red"
                                        onClick={() => void handleDelete(adGroup.id)}
                                    >
                                        Delete
                                    </Menu.Item>
                                </Menu.Dropdown>
                            </Menu>
                        ),
                    },
                ]}
                data={remote.withDefault(adGroups.data, [])}
                defaultSortColumn="Ad Group"
                pagination={{ pageSize: 20, showPagination: true }}
                selection={{
                    selectedIds,
                    onSelectionChange: setSelectedIds,
                    actions: [
                        {
                            label: 'Edit Selected',
                            onClick: () => setBulkEditing(true),
                            variant: 'outline',
                            startIcon: <IconPencil size={16} />,
                        },
                        {
                            label: 'Delete Selected',
                            onClick: handleBulkDelete,
                            variant: 'filled',
                            color: 'red',
                            startIcon: <IconTrash size={16} />,
                        },
                    ],
                }}
            />

            {/* Create / Duplicate Ad Group */}
            <Modal
                opened={createModal !== null}
                onClose={closeCreate}
                size="lg"
                title={createModal?.source ? 'Duplicate Ad Group' : 'Create Ad Group'}
            >
                <OperationErrorAlert error={createError} title="Failed to create ad group" />
                {platform && selectedCampaign && (
                    <CreateAdGroupForm
                        platform={platform}
                        campaign={selectedCampaign}
                        onSubmit={handleCreate}
                        onCancel={closeCreate}
                        isSubmitting={isSubmitting}
                        initialData={createModal?.source ? { name: `${createModal.source.name} (Copy)` } : undefined}
                    />
                )}
            </Modal>

            {/* Edit Ad Group */}
            <Modal opened={editing !== null} onClose={closeEdit} size="lg" title="Edit Ad Group">
                <OperationErrorAlert error={editError} title="Failed to update ad group" />
                {platform && editing && (
                    <EditAdGroupForm
                        platform={platform}
                        adGroup={editing}
                        onSubmit={handleUpdate}
                        onCancel={closeEdit}
                        isSubmitting={isSubmitting}
                    />
                )}
            </Modal>

            {/* Bulk Edit Ad Groups */}
            <Modal opened={bulkEditing} onClose={closeBulkEdit} size="lg" title="Bulk Edit Ad Groups">
                <OperationErrorAlert error={bulkEditError} title="Failed to update ad groups" />
                {platform && (
                    <BulkEditModal
                        platform={platform}
                        entityType="adGroup"
                        selectedCount={selectedIds.size}
                        onSubmit={handleBulkUpdate}
                        onCancel={closeBulkEdit}
                        isSubmitting={isSubmitting}
                    />
                )}
            </Modal>
        </>
    );
};
